import React, { useState } from "react";
import { X, RefreshCw } from "lucide-react";
import { CopyCredentials } from "./CopyCredentials";
import type { User, PasswordResetResponse } from "@/types";

interface PasswordResetModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onResetPassword: (userId: string) => Promise<PasswordResetResponse | null>;
}

/**
 * PasswordResetModal - Modal for resetting user passwords
 * Following SOLID principles with single responsibility and clear interface
 */
export const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  user,
  isOpen,
  onClose,
  onResetPassword,
}) => {
  const [isResetting, setIsResetting] = useState(false);
  const [resetResponse, setResetResponse] =
    useState<PasswordResetResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleReset = async () => {
    try {
      setIsResetting(true);
      setError(null);

      const response = await onResetPassword(user.user_id);
      if (response) {
        setResetResponse(response);
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } catch (err) {
      setError("An unexpected error occurred while resetting password.");
      console.error("Password reset error:", err);
    } finally {
      setIsResetting(false);
    }
  };

  const handleClose = () => {
    setResetResponse(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content password-reset-modal">
        <div className="modal-header">
          <h2>Reset Password</h2>
          <button
            onClick={handleClose}
            className="close-button"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {!resetResponse ? (
            /* Reset Confirmation */
            <div className="reset-confirmation">
              <div className="user-info">
                <h3>User Details</h3>
                <div className="user-details">
                  <div className="detail-item">
                    <span className="label">Name:</span>
                    <span className="value">{user.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email:</span>
                    <span className="value">{user.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">User ID:</span>
                    <span className="value">{user.user_id}</span>
                  </div>
                  {user.role && (
                    <div className="detail-item">
                      <span className="label">Role:</span>
                      <span className="value">{user.role}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="reset-warning">
                <p>
                  <strong>⚠️ Warning:</strong> This will generate a new password
                  for the user. The user will need to use this new password to
                  log in.
                </p>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  <span>{error}</span>
                </div>
              )}
            </div>
          ) : (
            /* Reset Success */
            <div className="reset-success">
              <div className="success-header">
                <div className="success-icon">✅</div>
                <h3>Password Reset Successfully!</h3>
                <p>
                  New password has been generated for {resetResponse.user_name}
                </p>
              </div>

              <CopyCredentials
                credentials={{
                  email: resetResponse.user_email,
                  password: resetResponse.password,
                }}
                title="New Credentials"
                className="reset-credentials"
              />

              <div className="reset-instructions">
                <h4>Next Steps:</h4>
                <ol>
                  <li>Copy the credentials using the button above</li>
                  <li>Share the new password with the user securely</li>
                  <li>Ask the user to change their password on first login</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!resetResponse ? (
            <div className="reset-actions">
              <button
                onClick={handleReset}
                disabled={isResetting}
                className="reset-button"
              >
                <RefreshCw
                  size={16}
                  className={isResetting ? "spinning" : ""}
                />
                {isResetting ? "Resetting..." : "Reset Password"}
              </button>
              <button
                onClick={handleClose}
                disabled={isResetting}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="success-actions">
              <button onClick={handleClose} className="done-button">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
